<template>
    <div class="max-w-2xl mx-auto px-4 py-6 sm:px-6 lg:px-8 space-y-6">
        <div class="flex items-center justify-between">
            <button @click="goBack" class="btn btn-secondary !p-2 !rounded-full">
                <span class="material-symbols-outlined">arrow_back</span>
            </button>
            <h1 class="text-xl font-bold text-slate-800 dark:text-slate-100">
                {{ isEditing ? 'Editar Financiamento' : 'Adicionar Financiamento' }}
            </h1>
            <button @click="handleSubmit" :disabled="isSubmitting" class="btn btn-primary min-w-[80px]">
                <span v-if="isSubmitting" class="material-symbols-outlined animate-spin text-lg">progress_activity</span>
                <span v-else>Salvar</span>
            </button>
        </div>

        <div class="panel p-5 sm:p-6">
            <form @submit.prevent="handleSubmit" class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-6">
                <div class="sm:col-span-2">
                    <label class="form-label">Descrição</label>
                    <input v-model="form.descricao" type="text" required placeholder="Ex: Financiamento Veículo" class="form-input" />
                </div>
                <div>
                    <label class="form-label">Valor Total (R$)</label>
                    <input v-model.number="form.valorTotal" type="number" step="0.01" required class="form-input" :disabled="isEditing && hasPaidInstallments" :class="{ 'form-input-readonly': isEditing && hasPaidInstallments }" />
                </div>
                <div>
                    <label class="form-label">Taxa de Juros Anual (%)</label>
                    <input v-model.number="form.taxaDeJurosAnual" type="number" step="0.01" placeholder="Ex: 9.99" class="form-input" :disabled="isEditing && hasPaidInstallments" :class="{ 'form-input-readonly': isEditing && hasPaidInstallments }" />
                </div>
                <div>
                    <label class="form-label">Prazo (Anos)</label>
                    <input v-model.number="form.numeroDeAnos" type="number" placeholder="Ex: 5" class="form-input" :disabled="isEditing && hasPaidInstallments" :class="{ 'form-input-readonly': isEditing && hasPaidInstallments }" />
                </div>
                <div>
                    <label class="form-label">Nº de Parcelas</label>
                    <input v-model.number="form.numeroParcelas" type="number" required class="form-input" :disabled="isEditing && hasPaidInstallments" :class="{ 'form-input-readonly': isEditing && hasPaidInstallments }" />
                </div>
                <div class="relative">
                    <label class="form-label">Valor da Parcela (R$)</label>
                    <input v-model.number="form.valorParcela" type="number" step="0.01" required class="form-input" :readonly="isValorParcelaCalculated || (isEditing && hasPaidInstallments)" :class="{ 'form-input-readonly': isValorParcelaCalculated || (isEditing && hasPaidInstallments) }" />
                    <span v-if="isValorParcelaCalculated || (isEditing && hasPaidInstallments)" class="material-symbols-outlined absolute right-3 top-9 text-slate-400 text-base" title="Campo bloqueado/calculado">lock</span>
                </div>
                <div>
                    <label class="form-label">Data Início Contrato</label>
                    <input v-model="form.dataInicio" type="date" required class="form-input" :disabled="isEditing && hasPaidInstallments" :class="{ 'form-input-readonly': isEditing && hasPaidInstallments }" />
                </div>
                <div class="sm:col-span-2">
                    <label class="form-label">Instituição Financeira</label>
                    <input v-model="form.instituicaoFinanceira" type="text" placeholder="Ex: Banco Itaú" class="form-input" />
                </div>
                 <p v-if="isEditing && hasPaidInstallments" class="sm:col-span-2 text-xs text-brand-600 dark:text-brand-500 p-3 bg-slate-50 dark:bg-brand-700/20 rounded-md">
                   Campos financeiros não podem ser alterados pois já existem parcelas pagas.
                 </p>
            </form>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import axios from 'axios';

const router = useRouter();
const route = useRoute();

const isEditing = computed(() => !!route.params.financiamentoId);
const isSubmitting = ref(false);
const hasPaidInstallments = ref(false); 

const getInitialFormData = () => ({
    id: null, descricao: '', valorTotal: null, numeroParcelas: null, valorParcela: null,
    dataInicio: new Date().toISOString().split('T')[0], instituicaoFinanceira: '',
    taxaDeJurosAnual: null, numeroDeAnos: null
});
const form = ref(getInitialFormData());

const isValorParcelaCalculated = computed(() => {
    const f = form.value;
    return f.valorTotal > 0 && f.numeroParcelas > 0 && f.taxaDeJurosAnual > 0 && !(isEditing.value && hasPaidInstallments.value);
});

watch(() => form.value.numeroDeAnos, (newAnos) => {
    if (newAnos > 0 && !(isEditing.value && hasPaidInstallments.value)) {
         form.value.numeroParcelas = newAnos * 12;
    }
});
watch([() => form.value.valorTotal, () => form.value.numeroParcelas, () => form.value.taxaDeJurosAnual], ([total, parcelas, taxa]) => {
     if (!(isEditing.value && hasPaidInstallments.value)) {
        form.value.valorParcela = calculateInstallment(total, parcelas, taxa);
     }
});


const API_BASE_URL = 'https://backend.controlease.com.br/api';
const authConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const calculateInstallment = (valorTotal, numParcelas, taxaAnual) => {
    if (!(valorTotal > 0 && numParcelas > 0)) return null;
    if (!taxaAnual || taxaAnual <= 0) return parseFloat((valorTotal / numParcelas).toFixed(2));
    const taxaMensal = taxaAnual / 12 / 100;
    const pmt = valorTotal * (taxaMensal * Math.pow(1 + taxaMensal, numParcelas)) / (Math.pow(1 + taxaMensal, numParcelas) - 1);
    return parseFloat(pmt.toFixed(2));
};

const fetchFinanciamento = async () => {
    if (!isEditing.value) return;
    try {
        const response = await axios.get(`${API_BASE_URL}/financiamentos/${route.params.financiamentoId}`, authConfig());
        const data = response.data;
        form.value = {
            id: data.id,
            descricao: data.descricao ?? '',
            instituicaoFinanceira: data.instituicaoFinanceira ?? '',
            valorTotal: data.valorTotal ?? null,
            numeroParcelas: data.numeroParcelas ?? null,
            valorParcela: data.valorParcela ?? null,
            dataInicio: data.dataInicio ?? '',
            taxaDeJurosAnual: data.taxaDeJurosAnual ?? null,
            numeroDeAnos: data.numeroDeAnos ?? null 
        };
        hasPaidInstallments.value = data.parcelasPagas > 0;
    } catch {
        alert('Erro ao carregar dados do financiamento.');
        router.push('/user/financiamentos');
    }
};

const handleSubmit = async () => {
    if (isSubmitting.value) return;
    isSubmitting.value = true;
    try {
        let response;
        if (isEditing.value) {
            response = await axios.put(`${API_BASE_URL}/financiamentos/${route.params.financiamentoId}`, form.value, authConfig());
            router.push({ name: 'FinanciamentosUser', params: { id: route.params.financiamentoId } });
        } else {
            response = await axios.post(`${API_BASE_URL}/financiamentos`, form.value, authConfig());
             if (response.data?.id) {
                router.push({ name: 'FinanciamentosUser', params: { id: response.data.id } });
            } else {
                router.push('/user/financiamentos');
            }
        }
    } catch (error) {
        alert(error.response?.data?.message || `Erro ao ${isEditing.value ? 'salvar alterações' : 'criar financiamento'}.`);
    } finally {
        isSubmitting.value = false;
    }
};

const goBack = () => {
     if (isEditing.value && route.params.financiamentoId) {
        router.push({ name: 'FinanciamentosUser', params: { id: route.params.financiamentoId } });
    } else {
        router.push('/user/financiamentos');
    }
};

onMounted(() => {
    if (isEditing.value) {
        fetchFinanciamento();
    } else {
        form.value = getInitialFormData();
    }
});
</script>

<style scoped>
.panel { @apply bg-white/70 dark:bg-brand-800/60 backdrop-blur-lg ring-1 ring-black/5 dark:ring-white/10 rounded-2xl shadow-xl shadow-slate-200/30 dark:shadow-black/20; }
.btn { @apply inline-flex items-center justify-center font-semibold py-2.5 px-5 rounded-xl transition-all duration-200 text-sm active:scale-95 disabled:opacity-60; }
.btn-primary { @apply btn bg-brand-600 hover:bg-brand-700 text-white shadow-lg shadow-brand-500/20; }
.btn-secondary { @apply btn bg-white/70 dark:bg-slate-800/50 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 ring-1 ring-black/5 dark:ring-white/10 shadow-sm; }
.form-input, .form-select { @apply w-full px-3.5 py-2.5 text-sm rounded-xl bg-white dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500; }
.form-input-readonly { @apply bg-slate-100 dark:bg-slate-700/50 cursor-not-allowed opacity-70; }
.form-label { @apply block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5; }
.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>